import { Badge, Icon, RadioBar, RadioBarItem, SkeletonSpinner, SvgIcon, Text } from '@civilgrid/ui/components';
import type { ProjectViewMode } from '../types';

type PageHeaderProps = {
  rowCount: number;
  viewMode: ProjectViewMode;
  onViewModeChange: (mode: ProjectViewMode) => void;
  isLoading?: boolean;
  /** When true, the view switcher (Split/Table/Map) is disabled. Used in new project mode. */
  viewSwitcherDisabled?: boolean;
  /** Id for the Projects heading, so the projects table can take its name from it. */
  titleId?: string;
};

/**
 * Header for the project landing page: title, project count, and view toggle (Table / Map / Split).
 * Uses only @civilgrid/ui components.
 */
export function PageHeader({
  rowCount,
  viewMode,
  onViewModeChange,
  isLoading = false,
  viewSwitcherDisabled = false,
  titleId,
}: PageHeaderProps) {
  return (
    <div className="flex items-center justify-between" data-testid="home-projects-header">
      <div className="flex items-center gap-3">
        <Text asChild className="text-primary text-xl font-semibold">
          <h1 id={titleId}>Projects</h1>
        </Text>
        <Badge kind="count">{isLoading ? <SkeletonSpinner size={18} /> : rowCount.toLocaleString()}</Badge>
      </div>
      <div className="flex items-center gap-2">
        <Text className="text-primary text-[10px]">View</Text>
        <RadioBar
          value={viewMode}
          onValueChange={(value: string) => onViewModeChange(value as ProjectViewMode)}
          size="small"
          disabled={viewSwitcherDisabled}
        >
          <RadioBarItem value="split" className="flex items-center gap-1" data-testid="home-view-mode-split">
            <Icon icon="viewVertical" size={14} />
            <Text className="text-[10px]">Split</Text>
          </RadioBarItem>
          <RadioBarItem value="table" className="flex items-center gap-1" data-testid="home-view-mode-table">
            <Icon icon="table" size={14} />
            <Text className="text-[10px]">Table</Text>
          </RadioBarItem>
          <RadioBarItem value="map" className="flex items-center gap-1" data-testid="home-view-mode-map">
            <SvgIcon icon="foldingMap" size={14} />
            <Text className="text-[10px]">Map</Text>
          </RadioBarItem>
        </RadioBar>
      </div>
    </div>
  );
}
